import {distinctUntilChanged, map, share} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {
  Agency, AppConfig, CompanyCategory,
  EmailParameter,
  GenericApiResponse,
  LoginParameters,
  LoginResult,
  ResetPasswordParameters,
  SecurityClient, SystemClient,
  SystemData, User, UserType
} from './api.client';
import {LocalStorageService} from './LocalStorageService';
import {DisplayEntity} from '../models/DisplayEntity';
import {StateResetRegistry} from './state-reset-registry.service';
import {CompanyCategoryService} from './companyCategory.service';

// 003-retire-ngrx (US1): the security slice moved off NgRx into this service.
// One BehaviorSubject holds the whole session state so multi-field transitions
// (loginSuccess) emit atomically, exactly like one store action did; public
// streams are the former selectors, projections ported verbatim with
// distinctUntilChanged to preserve the memoized-selector emission cadence.
interface SecuritySessionState {
  isAuthenticated: boolean;
  isLoggingIn: boolean;
  currentUser: User;
  currentDisplayEntity: DisplayEntity;
  loginMessage: string;
  systemData: SystemData;
  envName: string;
  defaultManagementAgency: Agency;
  appConfig: AppConfig;
}

const initialState: SecuritySessionState = {
  isAuthenticated: false,
  isLoggingIn: false,
  currentUser: null,
  currentDisplayEntity: null,
  loginMessage: null,
  systemData: null,
  envName: null,
  defaultManagementAgency: null,
  appConfig: null
};

@Injectable()
export class SecurityService {

  // Legacy storage key that used to persist the whole login result (JWT + PII).
  private static readonly LEGACY_LOGIN_RESULT_KEY = 'loginResult';

  redirectUrl = '';

  private readonly _state$ = new BehaviorSubject<SecuritySessionState>(initialState);

  // ---------------- Session streams (former security selectors) ----------------
  readonly systemData$ = this.select((s) => s.systemData);
  readonly isLoggingIn$ = this.select((s) => s.isLoggingIn);
  readonly isAuthenticated$ = this.select((s) => s.isAuthenticated);
  readonly loginMessage$ = this.select((s) => s.loginMessage);
  readonly currentUser$ = this.select((s) => s.currentUser);
  readonly currentUserUserType$ = this.select((s) => s.currentUser?.userType?.toString());
  readonly currentUserHasEfs$ = this.select((s) => s.currentUser?.hasEfs);
  readonly currentUserHasEfn$ = this.select((s) => s.currentUser?.hasEfn);
  readonly defaultManagementAgency$ = this.select((s) => s.defaultManagementAgency);
  readonly clientVerificationStalenessDays$ = this.select((s) => s.appConfig?.clientVerificationStalenessDays || 30);
  // systemData projections
  readonly kcStatuses$ = this.select((s) => s.systemData?.kcStatuses || []);
  readonly pledgeTypes$ = this.select((s) => s.systemData?.pledgeTypes);
  readonly prefix$ = this.select((s) => s.systemData?.prefix);
  readonly suffix$ = this.select((s) => s.systemData?.suffix);
  readonly states$ = this.select((s) => s.systemData?.states);
  readonly employmentStatus$ = this.select((s) => s.systemData?.employmentStatus);
  readonly unionInfo$ = this.select((s) => s.systemData?.unionInfo);
  readonly kcPrograms$ = this.select((s) => s.systemData?.kcPrograms);
  readonly kcFplAcceptanceRate$ = this.select((s) => s.systemData?.setting?.kcFplAcceptanceRate);
  readonly assistanceType$ = this.select((s) => s.systemData?.assistanceType);
  readonly region$ = this.select((s) => s.systemData?.region);
  readonly zones$ = this.select((s) => s.systemData?.zones);
  readonly gender$ = this.select((s) => s.systemData?.gender);
  readonly race$ = this.select((s) => s.systemData?.race);
  readonly administrativeAgencyFundAssistanceUhons$ = this.select((s) => s.systemData?.administrativeAgencyFundAssistanceUhons);
  readonly administrativeAgencyFundAssistanceUff$ = this.select((s) => s.systemData?.administrativeAgencyFundAssistanceUff);
  readonly administrativeAgencyFundAssistanceStorm$ = this.select((s) => s.systemData?.administrativeAgencyFundAssistanceStorm);
  readonly administrativeAgencyFundAssistanceSTLWater$ = this.select((s) => s.systemData?.administrativeAgencyFundAssistanceSTLWater);

  constructor(private _localStorageService: LocalStorageService,
              private _securityApiService: SecurityClient,
              private _systemApiService: SystemClient,
              private _companyCategoryService: CompanyCategoryService,
              private _resetRegistry: StateResetRegistry) {
    // FR-007: one-time startup purge of any legacy login result an upgrading user
    // still has in localStorage, so no stale JS-readable token/PII lingers after
    // the cookie-auth cutover. The session now lives only in the HttpOnly cookie
    // (server) + the in-memory state above (client).
    this._localStorageService.remove(SecurityService.LEGACY_LOGIN_RESULT_KEY);
  }

  // Re-derive the signed-in user after a page reload from the cookie-authenticated
  // GET /api/v1/security/me (the SPA no longer persists the login result). On
  // success the result is pushed into the in-memory state exactly like a fresh
  // login; a 401 (no/expired cookie) resolves false so the caller can redirect.
  // The /me body carries securityToken = null — the JWT stays in the HttpOnly cookie.
  rehydrateSession(): Observable<boolean> {
    return this._securityApiService.me().pipe(
      map((result) => {
        if (result?.currentUser) {
          this.loginSuccessAction(result);
          return true;
        }
        return false;
      }),
      catchError(() => of(false))
    );
  }

  // `rememberMe` is retained for call-site compatibility but no longer persists
  // anything: the cookie carries the session and its lifetime is set by the API
  // (SEC-6). Nothing token/PII-bearing is written to browser storage (FR-002).
  login(email: string, password: string, rememberMe: boolean = false): Observable<LoginResult> {
    if (!email) {
      throw new Error('Email not specified');
    }

    if (!password) {
      throw new Error('Password not specified');
    }
    this.loginInProgressAction();
    const parameters = new LoginParameters({email, password});
    const request = this._securityApiService.login(parameters).pipe(share());
    request.subscribe({ next: (result) => {
      this.loginSuccessAction(result);
    }, error: (err) => {
        if (err.isSwaggerException) {
            try {
                this.loginFailureAction(JSON.parse(err.response).message);
            } catch (e) {
            }
        } else {
            this.loginFailureAction(err);
        }
    }});
    return request;
  }

  sendPasswordResetEmail(email: string): Observable<GenericApiResponse | null> {
    return this._securityApiService.sendResetPasswordRequest(new EmailParameter({email}));
  }

  resetPassword(profileID: string, token: string, newPassword: string): Observable<GenericApiResponse | null> {
    return this._securityApiService.resetPassword(new ResetPasswordParameters({token, newPassword}));
  }

  loadSystemData(): Observable<SystemData> {
    const request = this._systemApiService.getSystemData().pipe(share());
    request.subscribe({ next: (data) => {
      this.updateSystemDataAction(data);
    }, error: () => {

    }});
    return request;
  }

  logout(): void {
    // The SPA cannot clear an HttpOnly cookie, so ask the API to clear it
    // (POST /api/v1/security/logout). This is best-effort and resilient: it may be
    // invoked from an already-invalid session, so a failure must not block the
    // local clean-up — in-memory state is always cleared.
    this._securityApiService.logout().subscribe({ next: () => {}, error: () => {} });
    // 003-retire-ngrx (D6): full state reset, replacing the old logoutAction that
    // only cleared two flags and left currentUser/systemData/appConfig in memory
    // (documented incidental fix). Resets this service and every registered
    // state-holder service.
    this._state$.next(initialState);
    this._resetRegistry.resetAll();
  }

  getSystemData(): SystemData {
    // Synchronous snapshot read (call-site compatibility; formerly a
    // subscribe-and-capture against the store).
    return this._state$.value.systemData;
  }

  // ---------------- Session transitions (former security actions) ------------------
  private loginSuccessAction(result: LoginResult): void {
    const currentUser = result.currentUser;
    const userType = currentUser.userType;
    const userAgency = result.userAgency;
    let displayEntity: DisplayEntity;

    if (userType === UserType.FundAdmin) {
        displayEntity = new DisplayEntity();
        displayEntity.entityType = 'company';
        displayEntity.entityId = currentUser.company.id;
        displayEntity.entityName = currentUser.company.name;
    } else if (userType === UserType.SystemAdmin) {
        displayEntity = new DisplayEntity();
        displayEntity.entityType = 'admin';
        displayEntity.entityName = 'System Admin';
    } else {
        const currentAgency = currentUser.agency;
        displayEntity = new DisplayEntity();
        displayEntity.entityType = 'agency';
        displayEntity.entityId = currentAgency.id;
        displayEntity.entityName = currentAgency.agencyName;
    }

    const user = User.fromJS({ ...currentUser, agency: userAgency });

    this.patch({
      appConfig: result.appConfig,
      isAuthenticated: true,
      currentUser: user,
      currentDisplayEntity: displayEntity,
      isLoggingIn: false,
      envName: result.environment
    });
    this.updateSystemDataAction(result.systemData);
    this.updateDefaultManagementAgency(result.defaultManagementAgency);
    this.updateCompanyCategories(result.systemData.categories);
  }

  private loginFailureAction(message: string): void {
    this.patch({
      isAuthenticated: false,
      loginMessage: message,
      isLoggingIn: false
    });
  }

  private loginInProgressAction(): void {
    this.patch({
      isAuthenticated: false,
      isLoggingIn: true
    });
  }

  private updateSystemDataAction(systemData: SystemData): void {
    this.patch({ systemData });
  }

  private updateDefaultManagementAgency(managementAgency: Agency): void {
    this.patch({ defaultManagementAgency: managementAgency });
  }

  private updateCompanyCategories(companyCategories: CompanyCategory[]): void {
    // 003-retire-ngrx (US3): the last cross-slice holdover released — the
    // companyCategory state now lives in CompanyCategoryService; this service
    // is store-free.
    this._companyCategoryService.setCompanyCategories(companyCategories);
  }

  // ---------------- State plumbing ----------------
  private select<T>(project: (state: SecuritySessionState) => T): Observable<T> {
    return this._state$.pipe(map(project), distinctUntilChanged());
  }

  private patch(partial: Partial<SecuritySessionState>): void {
    this._state$.next({ ...this._state$.value, ...partial });
  }
}
